/**
 * Poseidon dashboard — shared presentational primitives.
 *
 * These are intentionally dumb: they render exactly what they are given and
 * never invent a value. An unknown metric renders as a dashed "unavailable"
 * card, not as a zero, because a zero on a P&L screen is a claim.
 */
import type { ReactNode } from 'react';
import type { ApiError } from '../api/client';
import { AI_DISCLAIMER, AI_OUTPUT_LABEL } from '../lib/labels';
import { cx } from '../lib/cx';
import type { Tone } from '../lib/staleness';

export function Badge({
  tone = 'neutral',
  children,
  title,
  outline = false,
  large = false,
}: {
  tone?: Tone | 'neutral' | 'positive' | 'negative' | 'critical';
  children: ReactNode;
  title?: string;
  outline?: boolean;
  large?: boolean;
}): JSX.Element {
  return (
    <span className={cx('badge', `tone-${tone}`, outline && 'badge--outline', large && 'badge--lg')} title={title}>
      {children}
    </span>
  );
}

export function ProgressBar({
  fraction,
  tone = 'ok',
  title,
}: {
  fraction: number | null;
  tone?: Tone;
  title?: string;
}): JSX.Element {
  if (fraction === null) {
    return <span className="text-faint">—</span>;
  }
  const clamped = Math.max(0, Math.min(1, fraction));
  return (
    <div className="progress" title={title ?? `${(fraction * 100).toFixed(1)}% of the configured limit`}>
      <div className={cx('progress__fill', `tone-${tone}`)} style={{ width: `${(clamped * 100).toFixed(1)}%` }} />
    </div>
  );
}

export function Panel({
  title,
  subtitle,
  actions,
  children,
  footnote,
  flush = false,
  mock = false,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  footnote?: ReactNode;
  flush?: boolean;
  mock?: boolean;
}): JSX.Element {
  return (
    <section className="panel">
      <header className="panel__head">
        <h2 className="panel__title">{title}</h2>
        {subtitle !== undefined && <span className="panel__subtitle">{subtitle}</span>}
        {mock && <MockNote />}
        {actions !== undefined && <div className="panel__actions">{actions}</div>}
      </header>
      <div className={cx('panel__body', flush && 'panel__body--flush')}>{children}</div>
      {footnote !== undefined && <footer className="panel__foot">{footnote}</footer>}
    </section>
  );
}

/** Inline marker for a panel whose values are synthetic. */
export function MockNote({ label = 'mock values' }: { label?: string }): JSX.Element {
  return (
    <span className="mock-note" title="This panel is rendered from synthetic placeholder data. See src/mock/.">
      {label}
    </span>
  );
}

export function KeyValueList({ items }: { items: readonly { key: string; value: ReactNode }[] }): JSX.Element {
  return (
    <dl className="kv">
      {items.map((item) => (
        <div key={item.key} style={{ display: 'contents' }}>
          <dt className="kv__key">{item.key}</dt>
          <dd className="kv__value" style={{ margin: 0 }}>
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function EmptyState({ title, message }: { title: string; message?: string }): JSX.Element {
  return (
    <div className="empty-state">
      <strong>{title}</strong>
      {message !== undefined && <span>{message}</span>}
    </div>
  );
}

export function LoadingState({ label = 'Loading…' }: { label?: string }): JSX.Element {
  return (
    <div className="loading-state" role="status" aria-live="polite">
      {label}
    </div>
  );
}

export function ErrorState({
  error,
  context,
  onRetry,
}: {
  error: ApiError;
  context: string;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <div className="error-state" role="alert">
      <strong>{context}</strong>
      <span>{error.displayMessage}</span>
      <span className="text-mono" style={{ fontSize: 11 }}>
        {error.code}
        {error.correlationId !== null ? ` · correlation ${error.correlationId}` : ''}
      </span>
      {onRetry !== undefined && (
        <button type="button" className="btn btn--sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

/** A value the daemon has not supplied. Never rendered as zero. */
export function Unavailable({ reason }: { reason?: string | null }): JSX.Element {
  return (
    <span className="text-faint" title={reason ?? 'The daemon has not supplied this value.'}>
      —
    </span>
  );
}

/**
 * §3.2 E: AI output is a bounded research adjustment. This label is rendered
 * wherever model output appears so it can never read as a price prediction.
 */
export function AiResearchBadge(): JSX.Element {
  return (
    <span className="ai-badge" title={AI_DISCLAIMER}>
      {AI_OUTPUT_LABEL}
    </span>
  );
}

export function AiDisclaimer(): JSX.Element {
  return <p className="ai-disclaimer">{AI_DISCLAIMER}</p>;
}

export function Disclosure({ summary, children }: { summary: string; children: ReactNode }): JSX.Element {
  return (
    <details className="disclosure">
      <summary>{summary}</summary>
      <div style={{ marginTop: 8 }}>{children}</div>
    </details>
  );
}

export function BarList({
  items,
}: {
  items: readonly { label: string; value: number; max: number; display: string; tone?: Tone }[];
}): JSX.Element {
  return (
    <div className="bar-list">
      {items.map((item) => (
        <div className="bar-list__row" key={item.label}>
          <span className="text-muted">{item.label}</span>
          <span className="bar-list__track">
            <span
              className="bar-list__fill"
              style={{
                width: `${item.max === 0 ? 0 : Math.max(0, Math.min(100, (item.value / item.max) * 100)).toFixed(1)}%`,
                background: item.tone === 'danger' ? 'var(--danger)' : item.tone === 'warn' ? 'var(--warn)' : 'var(--series-1)',
              }}
            />
          </span>
          <span className="text-mono">{item.display}</span>
        </div>
      ))}
    </div>
  );
}
